import React, { useCallback, useEffect, useMemo } from 'react';
import { ResponseErrorPanel, TableColumn } from '@backstage/core-components';
import { CopyableText } from '../../CopyableText';
import { ModernResourceView } from '../../ModernResourceView';
import { AzureActionMenu, AzureActionConfirmationDialog } from '../actions';
import { AzureVmTableData } from './types';
import { mapResourcesToAzureVmTableData } from './utils';
import { ExecuteActionOptions } from '../../../api';
import {
  AzureVmResourceDetails,
  CRDAzureConfig,
} from '@internal/backstage-plugin-compute-resource-discovery-new-common';
import { useApi, errorApiRef, alertApiRef } from '@backstage/core-plugin-api';
import { useActionManagement } from './useActionManagement';
import { useAzureContext } from '../AzureContext';
import useAsyncRetry from 'react-use/lib/useAsyncRetry';

interface AzureTableProps {
  entityRef: string;
  azureConfig: CRDAzureConfig;
  title: string;
}

export const AzureVmTable: React.FC<AzureTableProps> = ({
  entityRef,
  azureConfig,
  title,
}) => {
  const { api, projectCode, refreshContext } = useAzureContext();

  const executeAction = useCallback(
    (options: ExecuteActionOptions) => {
      return api.executeAction(options);
    },
    [api],
  );

  const {
    value: resources,
    loading: isLoading,
    error: error,
    retry: refresh,
  } = useAsyncRetry(async () => {
    if (!projectCode) return [];
    return api.listResources({
      serviceType: 'azure',
      resourceType: 'vm',
      projectCode: projectCode,
      entityRef: entityRef,
      cloudConfig: azureConfig,
    });
  }, [projectCode, api]);

  const handleRefresh = () => {
    // Invalidate cache for this specific Azure VM configuration
    api.invalidateCache('listResources');
    refresh();
  };

  const errorApi = useApi(errorApiRef);
  const alertApi = useApi(alertApiRef);

  const {
    menuPosition,
    confirmationDialogOpen,
    selectedAction,
    isActionInProgress,
    handleTableActionClick,
    handleMenuClose,
    handleActionClick,
    handleConfirmAction,
    setConfirmationDialogOpen,
  } = useActionManagement({
    executeAction,
    refresh,
    errorApi,
    alertApi,
    azureConfig,
    entityRef,
    projectCode,
  });

  useEffect(() => {
    if (!refreshContext) return;

    const refreshKey = `azure:vm:${azureConfig.resourceGroup}`;
    const unregister = refreshContext.registerRefresh(refreshKey, refresh);

    return unregister;
  }, [refreshContext, refresh, azureConfig.resourceGroup]);

  // Columns for Azure
  const columns = useMemo<TableColumn<AzureVmTableData>[]>(
    () => [
      {
        title: 'ID',
        field: 'identifier',
        render: (row: AzureVmTableData) => (
          <div style={{ maxWidth: '400px' }}>
            <CopyableText value={row.identifier} />
          </div>
        ),
      },
      {
        title: 'Name',
        field: 'name',
      },
      {
        title: 'Size',
        field: 'size',
      },
      {
        title: 'Resource Group',
        field: 'resourceGroup',
      },
      {
        title: 'Status',
        field: 'state',
      },
    ],
    [],
  );

  const data = useMemo(
    () =>
      resources
        ? mapResourcesToAzureVmTableData(resources as AzureVmResourceDetails[])
        : [],
    [resources],
  );

  if (error) {
    error.message = `Failed to load Azure VMs`;
    return <ResponseErrorPanel error={error} title={error.message} />;
  }

  return (
    <>
      <ModernResourceView<AzureVmTableData>
        title={title}
        data={data}
        isLoading={isLoading}
        onRefresh={handleRefresh}
        onActions={handleTableActionClick}
        cloudService="azure"
        resourceType="vm"
        columns={columns}
        colorTheme="#0078D4" // Azure Blue
        emptyMessage="No VM instances found"
      />

      <AzureActionMenu
        handleActionClick={handleActionClick}
        handleMenuClose={handleMenuClose}
        handleOpenConnection={() => {}}
        menuPosition={menuPosition}
        service="vm"
      />

      <AzureActionConfirmationDialog
        open={confirmationDialogOpen}
        onClose={() => setConfirmationDialogOpen(false)}
        onConfirm={handleConfirmAction}
        action={selectedAction as any}
        isLoading={isActionInProgress}
        service="vm"
      />
    </>
  );
};
